import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { Slideshow, Download } from '@mui/icons-material';
import { API_URL, fetchWithCredentials } from '../config';
import InlineChatChart from './InlineChatChart';

const CreatePresentationModal = ({ open, onClose, chatMessages, chatQuestion, onPresentationCreated }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [audience, setAudience] = useState('');
  const [description, setDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSlides, setGeneratedSlides] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [error, setError] = useState(null);

  const steps = ['Presentation Details', 'Generate Slides', 'Preview & Export'];

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!audience.trim() || !description.trim()) {
        setError('Please fill in all fields');
        return;
      }
      setActiveStep(1);
      await generateSlides();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const generateSlides = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/presentations/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chatMessages,
          chatQuestion,
          audience,
          description,
          title: chatQuestion || 'Analysis Presentation',
          save: true  // Save to Firestore
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate slides');
      }

      const data = await response.json();
      setGeneratedSlides(data.slides);
      setCurrentSlideIndex(0);
    } catch (err) {
      console.error('Error generating slides:', err);
      setError(err.message || 'Error generating presentation');
      setIsGenerating(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportPowerPoint = async () => {
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/presentations/export-pptx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          slides: generatedSlides,
          title: chatQuestion || 'Analysis Presentation',
          audience,
          description
        })
      });

      if (!response.ok) {
        throw new Error('Failed to export presentation');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presentation_${Date.now()}.pptx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Save presentation reference
      if (onPresentationCreated) {
        onPresentationCreated({
          title: chatQuestion || 'Analysis Presentation',
          description,
          audience,
          slides: generatedSlides,
          createdDate: new Date().toISOString()
        });
      }

      onClose();
    } catch (err) {
      console.error('Error exporting presentation:', err);
      setError(err.message || 'Error exporting presentation');
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setAudience('');
    setDescription('');
    setGeneratedSlides(null);
    setCurrentSlideIndex(0);
    setError(null);
    onClose();
  };

  const currentSlide = generatedSlides?.[currentSlideIndex];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Slideshow color="primary" />
        Create Presentation
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Step 1: Presentation Details */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Provide details about your presentation
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Audience</InputLabel>
              <Select
                value={audience}
                label="Audience"
                onChange={(e) => setAudience(e.target.value)}
              >
                <MenuItem value="executives">Executives / C-Suite</MenuItem>
                <MenuItem value="marketing">Marketing Team</MenuItem>
                <MenuItem value="merchandising">Merchandising Team</MenuItem>
                <MenuItem value="finance">Finance & Planning</MenuItem>
                <MenuItem value="operations">Operations</MenuItem>
                <MenuItem value="board">Board of Directors</MenuItem>
                <MenuItem value="general">General Audience</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Presentation Description"
              placeholder="e.g., SKU optimization strategy for Q2 planning"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              helperText="Brief description of what this presentation covers"
            />

            <Typography variant="caption" color="text.secondary">
              Original question: {chatQuestion}
            </Typography>
          </Box>
        )}

        {/* Step 2: Generating Slides */}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            {isGenerating ? (
              <>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Generating presentation slides...
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ color: 'success.main' }}>
                  ✓ Slides Generated!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {generatedSlides?.length || 0} slides ready for preview
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Step 3: Preview & Export */}
        {activeStep === 2 && generatedSlides && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Slide Preview */}
            <Box sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 3,
              backgroundColor: '#f5f5f5',
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              {currentSlide && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                    Slide {currentSlideIndex + 1} of {generatedSlides.length}
                  </Typography>

                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                    {currentSlide.title}
                  </Typography>

                  {currentSlide.subtitle && (
                    <Typography variant="subtitle1" sx={{ mb: 2, color: 'text.secondary' }}>
                      {currentSlide.subtitle}
                    </Typography>
                  )}

                  {currentSlide.content && (
                    <Box sx={{ mb: 2 }}>
                      {Array.isArray(currentSlide.content) ? (
                        <ul style={{ margin: '0 0 0 20px', paddingLeft: 0 }}>
                          {currentSlide.content.map((item, idx) => (
                            <li key={idx} style={{ marginBottom: '8px', fontSize: '0.95rem' }}>
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Typography variant="body2">{currentSlide.content}</Typography>
                      )}
                    </Box>
                  )}

                  {currentSlide.chartData && currentSlide.chartConfig && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff', borderRadius: 1 }}>
                      <InlineChatChart
                        data={currentSlide.chartData}
                        chartType={currentSlide.chartConfig.type}
                        title={currentSlide.chartConfig.title}
                        description={currentSlide.chartConfig.description}
                        chartConfig={currentSlide.chartConfig}
                      />
                    </Box>
                  )}

                  {!currentSlide.chartData && currentSlide.chart && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Chart: {currentSlide.chart}
                      </Typography>
                    </Box>
                  )}

                  {currentSlide.notes && (
                    <Box sx={{ mt: 3, p: 2, backgroundColor: '#fffbea', borderRadius: 1, borderLeft: '4px solid #ff9800' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#f57c00' }}>
                        Notes for presenter:
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                        {currentSlide.notes}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Navigation */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
              <Button
                onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                disabled={currentSlideIndex === 0}
              >
                Previous Slide
              </Button>

              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {currentSlideIndex + 1} / {generatedSlides.length}
              </Typography>

              <Button
                onClick={() => setCurrentSlideIndex(Math.min(generatedSlides.length - 1, currentSlideIndex + 1))}
                disabled={currentSlideIndex === generatedSlides.length - 1}
              >
                Next Slide
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose}>Cancel</Button>

        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isGenerating}>
            Back
          </Button>
        )}

        {activeStep === 2 ? (
          <>
            <Button
              onClick={handleExportPowerPoint}
              variant="outlined"
              color="primary"
              startIcon={<Download />}
            >
              Export to PowerPoint
            </Button>
            <Button
              onClick={() => {
                if (onPresentationCreated) {
                  onPresentationCreated({
                    title: chatQuestion || 'Analysis Presentation',
                    description,
                    audience,
                    slides: generatedSlides,
                    createdDate: new Date().toISOString()
                  });
                }
                handleClose();
              }}
              variant="contained"
              color="primary"
            >
              OK
            </Button>
          </>
        ) : (
          <Button
            onClick={handleNext}
            variant="contained"
            color="primary"
            disabled={isGenerating || (activeStep === 0 && (!audience || !description))}
          >
            {isGenerating ? <CircularProgress size={24} /> : activeStep === 1 ? 'Preview' : 'Next'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreatePresentationModal;

